import React, { useState, useContext } from "react";
import {
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Typography,
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    ThemeProvider,
    useTheme
} from "@mui/material";
import { StoreContext } from "../context/StoreContext";
import { useAtom } from "jotai";
import {
    allLocationsAtom,
    allServicesAtom,
    allSkillsAtom,
    allUsersAtom,
    mbxUserAtom,
    showCompanySettingsModalAtom
} from "../atoms/atoms";
import { useNavigate } from "react-router-dom";
import {
    openServiceDialogAtom,
    selectedServiceIdAtom
} from "../atoms/servicesTable";
import {
    openUserDialogAtom,
    selectedUserIdAtom,
    userPageSnackbarAtom
} from "../atoms/usersTable";
import { sendReviewRequestEmail } from "../pages/checkout/modules/MessagingService";
import { colors } from "../utils/Constants";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LoadingButton from "@mui/lab/LoadingButton";
import { DialogTheme } from "../styles/DialogTheme";

const QuickStartGuide = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { actions } = useContext(StoreContext);
    const [mbxUser] = useAtom(mbxUserAtom);
    const [allLocations] = useAtom(allLocationsAtom);
    const [allServices] = useAtom(allServicesAtom);
    const [allSkills] = useAtom(allSkillsAtom);
    const [allUsers] = useAtom(allUsersAtom);
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);
    const [, setSelectedServiceId] = useAtom(selectedServiceIdAtom);
    const [, setOpenServiceDialog] = useAtom(openServiceDialogAtom);
    const [, setSelectedUserId] = useAtom(selectedUserIdAtom);
    const [, setOpenUserDialog] = useAtom(openUserDialogAtom);
    const [, setShowCompanySettingsModal] = useAtom(
        showCompanySettingsModalAtom
    );

    const [openReviewConfirmationModal, setOpenReviewConfirmationModal] =
        useState(false);
    const [skipQuickstartSkipSkills, setSkipQuickstartSkipSkills] = useState(
        localStorage.getItem("skipQuickstartSkills") === "true"
    );
    const [
        skipQuickstartSkipCompanyLocation,
        setSkipQuickstartSkipCompanyLocation
    ] = useState(
        localStorage.getItem("skipQuickstartCompanyLocation") === "true"
    );

    const reviewRequestSent =
        localStorage.getItem("reviewRequestSent") === "true";
    const settingsSaved = localStorage.getItem("settingsSaved") === "true";

    //Checklist steps
    const quickStartSteps = [
        {
            id: "create-locations",
            heading: "Create company locations",
            description: (
                <>
                    Add all company and virtual locations where your services
                    are available – mobile only businesses can{" "}
                    <span
                        style={{
                            textDecoration: "underline",
                            cursor: "pointer"
                        }}
                        onClick={() => {
                            setSkipQuickstartSkipCompanyLocation(true);
                            localStorage.setItem(
                                "skipQuickstartCompanyLocation",
                                "true"
                            );
                        }}
                    >
                        skip
                    </span>{" "}
                    this step
                </>
            ),
            onClick: () => handleOpenCompanyLocation(),
            isCompleted: () => getIsStepCompleted("create-locations")
        },
        {
            id: "add-services",
            heading: "Create services and service categories",
            description:
                "Create all the services that you offer and set prices, if applicable",
            onClick: () => handleOpenServices(),
            isCompleted: () => getIsStepCompleted("add-services")
        },
        {
            id: "add-skills",
            heading: "List available skills",
            description: (
                <>
                    Set up the skills that your providers will show on their
                    profiles, e.g. specific qualifications, <br />
                    languages spoken, etc – these skills are not required, so
                    you can{" "}
                    <span
                        style={{
                            textDecoration: "underline",
                            cursor: "pointer"
                        }}
                        onClick={() => {
                            setSkipQuickstartSkipSkills(true);
                            localStorage.setItem(
                                "skipQuickstartSkills",
                                "true"
                            );
                        }}
                    >
                        skip
                    </span>{" "}
                    this step if you wish
                </>
            ),
            optional: true,
            onClick: () => handleOpenSkills(),
            isCompleted: () => getIsStepCompleted("add-skills")
        },
        {
            id: "add-users",
            heading: "Add users and schedules",
            description:
                "Create as many providers, managers and admins as you need, and create a schedule for \nany user who will be taking bookings with clients",
            onClick: () => handleOpenUsers(),
            isCompleted: () => getIsStepCompleted("add-users")
        },
        {
            id: "check-settings",
            heading: "Configure your set-up",
            description:
                "Verify all settings, behaviours and email notifications, customize the online booking page with your \nown colours and branding, upload terms of service, and set up Stripe for payment processing, if applicable",
            onClick: () => handleOpenSettings(),
            isCompleted: () => getIsStepCompleted("check-settings")
        }
    ];

    //helper function to mark a step as "completed"
    const getIsStepCompleted = (stepId) => {
        return (
            {
                "create-locations":
                    allLocations?.length > 1 ||
                    skipQuickstartSkipCompanyLocation,
                "add-services": allServices?.length > 1,
                "add-skills": allSkills?.length > 1 || skipQuickstartSkipSkills,
                "add-users": allUsers?.length > 2,
                "check-settings": settingsSaved
            }[stepId] || false
        );
    };

    //HELPER FUNCTIONS for each step
    const handleOpenCompanyLocation = () => {
        actions.setMode("Add");
        let companyId = mbxUser.company.id;
        actions.setContextCompanyId(companyId);
        navigate("/services/locations/create");
    };

    const handleOpenServices = () => {
        setSelectedServiceId(null);
        setOpenServiceDialog(true);
    };

    const handleOpenSkills = () => {
        navigate("/skills/create");
    };

    const handleOpenUsers = () => {
        setSelectedUserId(null);
        setOpenUserDialog(true);
    };

    const handleOpenSettings = () => {
        actions.setMode("Edit");
        actions.setId(mbxUser.company?.id);
        setShowCompanySettingsModal(true);
    };

    const handleSendReviewRequest = () => {
        setUserPageSnackbar({
            open: true,
            message:
                "Our support team has been notified and will reach out shortly!",
            severity: "success"
        });
        sendReviewRequestEmail({
            fullName: mbxUser.firstname + " " + mbxUser.lastname,
            companyName: mbxUser.company?.name
        });
        localStorage.setItem("reviewRequestSent", "true");
    };

    return (
        <Box>
            <List disablePadding>
                {/* List of quickstart guide actions */}
                {quickStartSteps.map((step, index) => {
                    const completed = step.isCompleted();
                    const textDecorationStyle = completed ? "line-through" : "";
                    const headerColor = completed
                        ? "text.disabled"
                        : colors.MB_INFO_COLOR_MAIN;
                    const descriptionColor = completed
                        ? "text.disabled"
                        : "text.secondary";

                    return (
                        <React.Fragment key={step.id}>
                            <ListItem
                                alignItems="flex-start"
                                disablePadding
                                sx={{ py: 1, alignItems: "center" }}
                            >
                                {/*Number Circle */}
                                <ListItemIcon sx={{ minWidth: 40, mt: 0 }}>
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "50%",
                                            backgroundColor: "primary.main",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "white",
                                            fontWeight: 700
                                        }}
                                    >
                                        {index + 1}
                                    </Box>
                                </ListItemIcon>

                                {/*Header and Subheader Text */}
                                <ListItemText>
                                    {/* Header */}
                                    <Typography
                                        variant="body2"
                                        component="div"
                                        onClick={step.onClick}
                                        sx={{
                                            cursor: "pointer",
                                            color: headerColor,
                                            "&:hover": {
                                                color: completed
                                                    ? headerColor
                                                    : "primary.main"
                                            },
                                            width: "fit-content",
                                            mb: 0,
                                            display: "flex"
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            component="div"
                                            sx={{
                                                fontWeight: 700,
                                                textDecoration: `underline ${textDecorationStyle}`
                                            }}
                                        >
                                            {step.heading}
                                        </Typography>
                                        {step.optional && (
                                            <Typography
                                                variant="body2"
                                                component="div"
                                                sx={{
                                                    whiteSpace: "pre-wrap",
                                                    color: descriptionColor,
                                                    textDecoration: `${textDecorationStyle}`
                                                }}
                                            >{` (optional)`}</Typography>
                                        )}
                                    </Typography>

                                    {/*Description*/}
                                    <Typography
                                        variant="body2"
                                        color={descriptionColor}
                                        sx={{
                                            textDecoration: textDecorationStyle,
                                            color: descriptionColor,
                                            fontWeight: 400,
                                            whiteSpace: "pre-line"
                                        }}
                                    >
                                        {step.description}
                                    </Typography>
                                </ListItemText>
                            </ListItem>
                        </React.Fragment>
                    );
                })}

                {/* Request a review link */}
                {!reviewRequestSent && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "column",
                            gap: 2.25,
                            mt: 2.25
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={() => setOpenReviewConfirmationModal(true)}
                            endIcon={<ArrowForwardIcon />}
                            disableElevation
                        >
                            Request a review from our support team
                        </Button>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                                width: "fit-content"
                            }}
                        >
                            Our support team will review your account and make
                            recommendations before going live
                        </Typography>
                    </Box>
                )}
            </List>
            {openReviewConfirmationModal && (
                <ThemeProvider theme={DialogTheme(theme)}>
                    <SubmitReviewRequestModal
                        open={openReviewConfirmationModal}
                        onClose={() => setOpenReviewConfirmationModal(false)}
                    />
                </ThemeProvider>
            )}
        </Box>
    );
};

const SubmitReviewRequestModal = ({ open, onClose }) => {
    const [mbxUser] = useAtom(mbxUserAtom);
    const [, setUserPageSnackbar] = useAtom(userPageSnackbarAtom);

    const [sendingReview, setSendingReview] = useState(false);

    const handleSendReviewRequest = () => {
        try {
            setUserPageSnackbar({
                open: true,
                message:
                    "Our support team has been notified and will reach out shortly!",
                severity: "success"
            });
            sendReviewRequestEmail({
                fullName: mbxUser.firstname + " " + mbxUser.lastname,
                companyName: mbxUser.company?.name
            });
            localStorage.setItem("reviewRequestSent", "true");
        } catch (e) {
            console.log("Error sending review: ", e);
        } finally {
            setSendingReview(false);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle sx={{ paddingBottom: "32px" }}>
                Submit review request
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ paddingBottom: "18px" }}>
                    Are you sure you wish to submit your review request to our
                    support team?
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                    This can only be done once
                </Typography>
            </DialogContent>
            <DialogActions sx={{ paddingTop: "32px" }}>
                <Grid display={"flex"} gap={"18px"}>
                    <Button
                        variant="outlined"
                        onClick={() => onClose()}
                        color="primary"
                    >
                        Cancel
                    </Button>

                    <LoadingButton
                        variant="contained"
                        onClick={() => {
                            handleSendReviewRequest();
                        }}
                        color="primary"
                        autoFocus
                        disabled={sendingReview}
                        loading={sendingReview}
                    >
                        Submit request
                    </LoadingButton>
                </Grid>
            </DialogActions>
        </Dialog>
    );
};

export default QuickStartGuide;
